import dayjs from 'dayjs'
import ignore from 'ignore'
import pako from 'pako'
import {
  removeDirHandle,
  selectHandleFolderHash,
  selectUserHandleById,
  updateFolderHash,
  updateSecurityStatus
} from '~/store/dirHandleSlice'
import { FileHandleStore } from '~/utils/FileHandleStore'
import { FolderHasher } from '~/utils/folder-hasher'
import { getMandatoryExceptionsString } from '~/utils/uploadIgnoreRules'
import { TarWriter } from './tarjs'

// Update imports to use localSyncService
import {
  SecurityScanStatus,
  deleteLocalSyncRepo,
  syncLocalSyncRepos,
  uploadLocalSyncRepo
} from '~/services/localSyncService'
import { AppEvents, globalEventEmitter } from '~/utils/shared'

// Store injection pattern to avoid circular dependency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _store: { getState: () => any } | null = null

export const setCronSchedulerStore = (store: typeof _store) => {
  _store = store
}

const getStore = () => {
  if (!_store) {
    throw new Error('Store not initialized. Call setCronSchedulerStore() first.')
  }
  return _store
}

class CronScheduler {
  private fileHandleStore: FileHandleStore
  private folderHasher: FolderHasher
  private interval: NodeJS.Timeout | null = null
  private processingInProgress: boolean = false
  private ig: ReturnType<typeof ignore>
  private workspaceUuid: string | null = null

  constructor(private checkIntervalSeconds: number = 1) {
    this.fileHandleStore = new FileHandleStore()
    this.folderHasher = new FolderHasher()
    this.ig = ignore().add(getMandatoryExceptionsString())
  }

  /**
   * Set the workspace UUID for workspace-aware operations
   */
  setWorkspaceUuid(workspaceUuid: string): void {
    this.workspaceUuid = workspaceUuid
  }

  /**
   * Sync repositories with the server
   */
  private async syncRepositoriesWithServer(): Promise<void> {
    if (!this.workspaceUuid) {
      console.warn('🔄 Cannot sync - no workspace context available')
      return
    }

    try {
      const state = getStore().getState()
      const userEmail = state.dirHandle._currentUserEmail
      if (!userEmail) {
        console.warn('🔄 Cannot sync - no user initialized')
        return
      }

      const userState = state.dirHandle.userStates[userEmail]
      if (!userState?.isInitialized) {
        console.warn('🔄 Cannot sync - user state not initialized')
        return
      }

      // Get valid handle names
      const validHandleNames = userState.handles
        .map(handle => handle.name)
        .filter(Boolean) as string[]

      const deviceId = window.getDeviceId()
      await syncLocalSyncRepos(this.workspaceUuid, deviceId, validHandleNames)
      console.info(`🔄 Synced ${validHandleNames.length} repositories with server`)
    } catch (error) {
      // Silently handle network errors (e.g., server unreachable)
      const isNetworkError =
        error instanceof TypeError && (error as TypeError).message === 'Failed to fetch'
      if (!isNetworkError) {
        console.error('Failed to sync repositories with server:', error)
      }
    }
  }

  /**
   * Helper method to update security status consistently
   */
  private updateSecurityStatus(handleId: string, status: SecurityScanStatus): void {
    updateSecurityStatus(handleId, status)
  }

  /**
   * Helper method to normalize virus scan status
   */
  private normalizeSecurityStatus(status: string): SecurityScanStatus {
    if (status === 'NO_THREATS_FOUND' || status === 'SCAN_SKIPPED') {
      return 'CLEAN'
    }
    return status as SecurityScanStatus
  }

  async start() {
    if (this.interval) {
      console.info('CronScheduler already running, skipping start()')
      return
    }

    try {
      await this.fileHandleStore.init()

      // First ensure we've synchronized with the server
      if (this.workspaceUuid) {
        await this.syncRepositoriesWithServer()
      }

      // Force process all directories at startup to ensure they're synced
      await this.forceProcessDirectories()

      // Schedule subsequent runs with normal processing
      this.interval = setInterval(() => this.processDirectories(), this.checkIntervalSeconds * 1000)

      // Emit initialization complete event to trigger initial indexes fetch
      // This ensures IndexesContext fetches data even if no local repos are processed
      setTimeout(() => {
        globalEventEmitter.emit(AppEvents.IndexesUpdated)
      }, 1000) // Small delay to ensure all initialization is truly complete
    } catch (error) {
      console.error('Failed to start CronScheduler:', error)

      // Even if cronScheduler fails, trigger indexes fetch so users can still see remote repos
      setTimeout(() => {
        globalEventEmitter.emit(AppEvents.IndexesUpdated)
      }, 1000)

      throw error
    }
  }

  stop() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
      console.info('CronScheduler stopped successfully')
    } else {
      console.info('CronScheduler was not running, no need to stop')
    }
  }

  /**
   * Check if the scheduler is currently running
   */
  isRunning(): boolean {
    return this.interval !== null
  }

  /**
   * Force process all directories regardless of hash changes
   * This ensures all repositories are uploaded at startup
   */
  private async forceProcessDirectories() {
    if (this.isRunning()) {
      return
    }
    try {
      const state = getStore().getState()
      const currentUserEmail = state.dirHandle._currentUserEmail
      if (!currentUserEmail) {
        return // No user logged in
      }

      const userState = state.dirHandle.userStates[currentUserEmail]
      if (!userState || !userState.isInitialized) {
        return // User state not initialized
      }

      console.info('Force processing all repositories at startup...')

      // Process all handles belonging to current user
      for (const { id } of userState.handles) {
        try {
          const storedHandle = await this.fileHandleStore.getHandleById(id)
          if (storedHandle) {
            // Check if folder still exists on disk
            const folderExists = await this.fileHandleStore.isFolderExists(id)
            if (!folderExists) {
              console.warn(`Directory for handle ${id} no longer exists on disk. Removing handle.`)
              await this.removeNonExistentFolder(id, storedHandle.handle.name)
              continue // Skip processing this directory
            }

            // Process directory with force flag
            await this.processDirectory(storedHandle.handle, id, true)
          }
        } catch (error) {
          console.error(`Error processing directory handle ${id}:`, error)
        }
      }

      console.info('Initial repository processing complete')

      // After all directories are processed, sync with server one more time
      // This ensures the server has the latest list of repositories
      if (this.workspaceUuid) {
        await this.syncRepositoriesWithServer()
      }
    } finally {
      // Processing complete - no state to reset since we use interval to track running state
    }
  }

  private async processDirectories() {
    // Use a local flag to prevent concurrent runs of this method
    if (this.processingInProgress) {
      return
    }

    this.processingInProgress = true
    try {
      // Get current user's handles from Redux store
      const state = getStore().getState()
      const currentUserEmail = state.dirHandle._currentUserEmail
      if (!currentUserEmail) {
        return // No user logged in
      }

      const userState = state.dirHandle.userStates[currentUserEmail]
      if (!userState || !userState.isInitialized) {
        return // User state not initialized
      }

      // Process only handles belonging to current user
      for (const { id } of userState.handles) {
        try {
          const storedHandle = await this.fileHandleStore.getHandleById(id)
          if (storedHandle) {
            // Check if folder still exists on disk
            const folderExists = await this.fileHandleStore.isFolderExists(id)
            if (!folderExists) {
              console.warn(`Directory for handle ${id} no longer exists on disk. Removing handle.`)
              // Call handleRemoveLocation from inside CronScheduler
              await this.removeNonExistentFolder(id, storedHandle.handle.name)
              continue // Skip processing this directory
            }

            // Check if this repository has security threats - skip processing if it does
            const handleInfo = userState.handles.find(h => h.id === id)
            if (handleInfo?.securityStatus === 'THREATS_FOUND') {
              console.warn(`Skipping directory ${storedHandle.handle.name} with security threats`)
              continue // Skip processing this directory
            }

            // Process directory normally (not forced)
            await this.processDirectory(storedHandle.handle, id, false)
          }
        } catch (error) {
          console.error(`Error processing directory handle ${id}:`, error)
        }
      }
    } finally {
      this.processingInProgress = false
    }
  }

  public async processDirectory(
    directoryHandle: FileSystemDirectoryHandle,
    handleId: string,
    forceUpload: boolean = false
  ) {
    // Check if this repository has security threats - skip processing if it does
    const state = getStore().getState()
    const handleInfo = selectUserHandleById(state, handleId)
    if (handleInfo?.securityStatus === 'THREATS_FOUND') {
      console.warn(`Skipping directory ${directoryHandle.name} with security threats`)
      return // Skip processing this directory
    }

    const currentHash = await this.folderHasher.getFolderHash(directoryHandle)

    // Get current handle's hash using selector with default current user
    const lastFolderHash = selectHandleFolderHash(state, handleId)

    // If the hash hasn't changed and we're not forcing upload, skip the process
    if (lastFolderHash === currentHash && !forceUpload) {
      return
    }

    const files: File[] = []
    let totalFileCount = 0
    const MAX_FILES = 10000 // Prevent memory issues with very large repositories

    // Recursively collect all files with optimizations
    const collectFiles = async (handle: FileSystemDirectoryHandle, path: string = '') => {
      if (totalFileCount >= MAX_FILES) {
        console.warn(`Reached maximum file limit (${MAX_FILES}) for ${directoryHandle.name}`)
        return
      }

      try {
        const entries = []
        // Type assertion for FileSystemDirectoryHandle.values() - this is a known API
        const handleWithValues = handle as FileSystemDirectoryHandle & {
          values(): AsyncIterable<FileSystemHandle>
        }
        for await (const entry of handleWithValues.values()) {
          entries.push({ entry, path: path ? `${path}/${entry.name}` : entry.name })
        }

        // Sort entries to process files before directories for better progress feedback
        entries.sort((a, b) => {
          if (a.entry.kind === 'file' && b.entry.kind === 'directory') return -1
          if (a.entry.kind === 'directory' && b.entry.kind === 'file') return 1
          return 0
        })

        for (const { entry, path: entryPath } of entries) {
          if (totalFileCount >= MAX_FILES) break

          // Skip if path should be excluded based on folder hasher rules
          if (!this.folderHasher.shouldIncludePath(entryPath)) {
            continue
          }

          if (entry.kind === 'file') {
            try {
              totalFileCount++
              const fileHandle = entry as FileSystemFileHandle
              const file = await fileHandle.getFile()

              // Skip very large files (>100MB) to prevent memory issues
              if (file.size > 100 * 1024 * 1024) {
                console.warn(
                  `Skipping large file ${entryPath} (${Math.round(file.size / 1024 / 1024)}MB)`
                )
                continue
              }

              const newFile = new File([file], file.name, { type: file.type })
              Object.defineProperty(newFile, 'webkitRelativePath', {
                writable: true,
                value: entryPath
              })
              files.push(newFile)
            } catch (fileError) {
              console.error(`Error reading file ${entryPath}:`, fileError)
            }
          } else if (entry.kind === 'directory') {
            const dirHandle = entry as FileSystemDirectoryHandle
            await collectFiles(dirHandle, entryPath)
          }
        }
      } catch (dirError) {
        console.error(`Error reading directory ${path || handle.name}:`, dirError)
      }
    }

    try {
      await collectFiles(directoryHandle)

      if (files.length === 0) {
        throw new Error(
          `The folder "${directoryHandle.name}" is empty or contains no valid files. Please select a folder that contains files.`
        )
      }

      const tarFile = await this.createCompressedTarArchive(files, directoryHandle.name, handleId)
      // Update security status to SCANNING
      this.updateSecurityStatus(handleId, 'SCANNING')

      // Upload the archive using the new service
      // Skip upload if workspace context is not available
      if (!this.workspaceUuid) {
        console.warn(
          `🔄 Workspace context not available - skipping upload for ${directoryHandle.name}`
        )
        return
      }

      await uploadLocalSyncRepo(this.workspaceUuid, tarFile, {
        onVirusScanStatus: status => {
          // Update security status in Redux store
          const normalizedStatus = this.normalizeSecurityStatus(status)
          this.updateSecurityStatus(handleId, normalizedStatus)
        },
        onComplete: async (path, virusScanStatus) => {
          // Handle virus scan status if provided
          if (virusScanStatus) {
            const normalizedStatus = this.normalizeSecurityStatus(virusScanStatus)
            this.updateSecurityStatus(handleId, normalizedStatus)
          }

          // Only update folder hash if security status is clean
          if (virusScanStatus !== 'THREATS_FOUND') {
            // Update folder hash
            updateFolderHash(handleId, currentHash)

            // Emit event to trigger indexes refetch
            globalEventEmitter.emit(AppEvents.IndexesUpdated)
          } else {
            console.warn(
              `Security threats detected in ${directoryHandle.name}, repository will not be synchronized`
            )
          }
        },
        onError: (error, virusScanStatus) => {
          console.error(`Error uploading ${tarFile.name}:`, error)

          // Handle virus scan status from error
          if (virusScanStatus === 'THREATS_FOUND') {
            this.updateSecurityStatus(handleId, 'THREATS_FOUND')
            console.warn(`Security threat detected in ${directoryHandle.name}`)

            // Don't throw an exception for security threats - just report them and stop
            return
          }

          // Only throw for non-security related errors
          throw new Error(`Upload failed: ${error}`)
        }
      })
    } catch (error: unknown) {
      // If this is a virus detection error, handle it without re-throwing
      if (
        error instanceof Error &&
        (error.message.includes('Virus') || error.message.includes('security threat'))
      ) {
        console.warn(
          `Virus or security threat detected in ${directoryHandle.name}, repository will not be synchronized`
        )

        // Update security status
        this.updateSecurityStatus(handleId, 'THREATS_FOUND')

        // Don't rethrow the error
        return
      }

      // Log and rethrow other types of errors
      console.error(`🔄 Failed to process ${directoryHandle.name}:`, error)
      throw error
    }
  }

  private async createCompressedTarArchive(
    files: File[],
    directoryName: string,
    handleId: string
  ): Promise<File> {
    const tarWriter = new TarWriter()

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const fileContent = await file.arrayBuffer()
        const relativePath = file.webkitRelativePath

        // Skip files that match gitignore patterns
        if (!this.ig.ignores(relativePath)) {
          // Wrap all files within the directory name
          const pathInArchive = `${directoryName}/${relativePath}`
          await tarWriter.addFile(pathInArchive, new Uint8Array(fileContent))
        }
      } catch (error) {
        console.error(`Error processing file ${file.webkitRelativePath}:`, error)
      }
    }

    const tarBlob = await tarWriter.write()
    const tarArray = new Uint8Array(await tarBlob.arrayBuffer())

    // Convert to standard array buffer and then gzip
    const arrayBuffer = tarArray.buffer
    const gzippedArray = pako.gzip(new Uint8Array(arrayBuffer))

    const date = dayjs().format('YYYYMMDDHHmmss')
    const tarFileName = `${directoryName}.tar.gz`
    const newTarFileName = `${date}-${handleId}___${tarFileName}`

    // Create file with the proper BlobPart[] type
    return new File([new Uint8Array(gzippedArray)], newTarFileName, {
      type: 'application/gzip'
    })
  }

  private async removeNonExistentFolder(id: string, dirName: string) {
    try {
      // Remove from Redux store
      await removeDirHandle(id)

      // Remove uploads from the S3 bucket using new service
      try {
        if (this.workspaceUuid) {
          await deleteLocalSyncRepo(this.workspaceUuid, dirName)
        } else {
          console.warn(`🔄 Workspace context not available - skipping deletion of ${dirName}`)
        }
      } catch (deleteError) {
        console.error(`Failed to delete remote data for ${dirName}:`, deleteError)
      }
    } catch (error) {
      console.error(`Failed to clean up handle for non-existent folder ${id}:`, error)
    }
  }
}

// Create and export a singleton instance
export const cronScheduler = new CronScheduler()

// Export the class for testing or custom initialization
export { CronScheduler }
